import {
  AccordionDetails,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { ReminderSettingDialog } from "./ReminderSettingDialog";
import { getNotificationDataById } from "../utility/getNotificationData";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { deleteNotificationDataById } from "../utility/deleteNotificationData";
import { registerPushSubscription } from "../notificationComponents/getVapId";
import { putPushSubscriptionData } from "../utility/putPushSubscriptionData";
import { getPushSubscriptionData } from "../utility/getPushSubscriptionData";
import { deleteEventById } from "../utility/deleteEvent";
import { mainTextColor } from "../utility/colorPalette";
import { useAuth } from "../../context/AuthContext";

interface ReminderData {
  userId: string;
  notificationIsOn: boolean;
  notificationTime: string;
}

export const ReminderDetailComponent = () => {
  const { user } = useAuthenticator((context) => [context.user]);
  const { idToken, refreshAuthSession } = useAuth();
  const [reminderSettings, setReminderSettings] = useState<ReminderData | null>(
    null
  );
  const [settingDialogIsOn, setSettingDialogIsOn] = useState<boolean>(false);

  // リマインダ情報の削除に失敗したとき
  const [isDeleteError, setIsDeleteError] = useState<boolean>(false);
  // 通知がブロックされているとき
  const [isNotificationDenied, setIsNotificationDenied] =
    useState<boolean>(false);

  // 最初の読み込みの完了検知
  const [isLoadingComplete, setIsLoadingComplete] = useState<boolean>(false);

  // 削除処理が実行中
  const [deleteActivate, setDeleteActivate] = useState<boolean>(false);

  useEffect(() => {
    if (idToken) {
      getNotificationDataById(user.userId, idToken).then((data) => {
        setReminderSettings(data);
        setIsLoadingComplete(true);
      });
    }
  }, []);

  // 削除ボタン
  const deleteButtonClicked = () => {
    setDeleteActivate(true);
    setIsDeleteError(false);
    // DBの削除
    if (!idToken) return;
    deleteNotificationDataById(user.userId, idToken).then((data) => {
      if (data.status === 200) {
        setReminderSettings(null);
      } else {
        setIsDeleteError(true);
      }
      setDeleteActivate(false);
    });
    // イベントの削除
    deleteEventById(user.userId, idToken);
  };

  // 設定ボタンが押されたときにサブスクリプション情報を取得、保存する
  const settingsButtonClicked = async () => {
    try {
      // １．現在の通知権限を取得
      let permission = Notification.permission; // "granted" | "denied" | "default"

      // この時点で拒否されているときはエラーを出す
      if (permission === "denied") {
        setIsNotificationDenied(true);
      } else {
        setIsNotificationDenied(false);
      }

      console.log(permission);
      // ２．まだ許可されていなければリクエスト
      if (permission !== "granted") {
        permission = await Notification.requestPermission();
      }

      // ３．許可されていればプッシュ登録＆サーバー送信、ダイアログＯＮ
      if (permission === "granted") {
        if (!idToken) return;
        const subscription = await registerPushSubscription();
        await putPushSubscriptionData(
          {
            userId: user.userId,
            subscription,
          },
          idToken
        );
        setSettingDialogIsOn(true);
      } else {
        // 拒否 or デフォルト（ユーザーが「あとで」にした等）の場合
        console.warn("通知の許可が得られませんでした。設定画面を開けません。");
        // 必要ならユーザーに案内ポップアップ等を出す
      }
    } catch (err) {
      console.error("プッシュ登録中にエラーが発生しました", err);
    }
  };

  const deniedAlert = (
    <Alert severity="error" sx={{ my: 1 }}>
      <Typography variant="body2">
        通知がブロックされています。リマインダを受け取るにはブラウザの設定から許可をしてください。
      </Typography>
    </Alert>
  );

  return (
    <>
      <AccordionDetails>
        {!isLoadingComplete && (
          <Box>
            <Typography variant="body2" color={mainTextColor}>
              データを読み込んでいます
            </Typography>
            <CircularProgress />
          </Box>
        )}
        {isLoadingComplete && (
          <Box>
            {!reminderSettings?.notificationIsOn && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography>登録されていません</Typography>
                <Button variant="contained" onClick={settingsButtonClicked}>
                  設定
                </Button>
              </Box>
            )}
            {isNotificationDenied && deniedAlert}
            {reminderSettings?.notificationIsOn && (
              <>
                {isDeleteError && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    削除に失敗しました。
                  </Alert>
                )}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography>
                    {reminderSettings.notificationTime}に設定されています
                  </Typography>
                  <Button
                    color="error"
                    variant="outlined"
                    onClick={deleteButtonClicked}
                  >
                    削除
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}

        <Typography variant="caption">
          リマインダを登録することで設定した時間に通知が届き、実施忘れを防止することが出来ます。
        </Typography>
      </AccordionDetails>
      <ReminderSettingDialog
        isOn={settingDialogIsOn}
        setIsOn={setSettingDialogIsOn}
        setReminderSettings={setReminderSettings}
      />
      <Dialog open={deleteActivate}>
        <DialogContent sx={{ textAlign: "center" }}>
          <Typography variant="body2" color={mainTextColor}>
            設定を削除しています
          </Typography>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    </>
  );
};
